import React, {useCallback, useEffect, useState} from "react";
import {useLocation, useNavigate} from "react-router-dom";

const API = "/api/staff/lives";

async function request(path, options = {}) {
  const token = localStorage.getItem("evoAccessToken") || "";
  const response = await fetch(`${API}${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      ...(options.headers || {})
    }
  });
  const data = await response.json();
  if (!response.ok || !data.success) {
    throw new Error(data.error || data.message || "Action impossible.");
  }
  return data;
}

export default function StaffLiveCasesPage({kind = "reports"}) {
  const navigate = useNavigate();
  const location = useLocation();
  const admin = location.pathname.startsWith("/admin/");
  const [cases, setCases] = useState([]);
  const [reasons, setReasons] = useState({});
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState("");
  const title = kind === "chat" ? "Chat live" : "Lives signalés";

  const refresh = useCallback(async () => {
    try {
      const data = await request(`/${kind}`);
      setCases(Array.isArray(data.cases) ? data.cases : []);
      setError("");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [kind]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const decide = async (item, action) => {
    const reason = String(reasons[item.id] || "").trim();
    if (action !== "resolve" && !reason) {
      setError("Indique le motif de l’avertissement ou de la coupure.");
      return;
    }
    if (action === "stop" && !window.confirm("Couper ce live maintenant ?")) {
      return;
    }

    setBusyId(item.id);
    setError("");
    try {
      if (action !== "resolve") {
        await request(`/${item.live_id}/${action}`, {
          method: "POST",
          body: JSON.stringify({reason})
        });
      }
      await request(`/alerts/${item.id}/resolve`, {
        method: "POST",
        body: JSON.stringify({reason: reason || "Dossier examiné"})
      });
      await refresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const button = (item, action, label, color) => (
    <button
      type="button"
      disabled={busyId !== null}
      onClick={() => decide(item, action)}
      style={{
        border: `1px solid ${color}`, background: "#071c2b", color,
        borderRadius: 9, padding: "10px 13px", fontWeight: 800,
        cursor: busyId === null ? "pointer" : "wait"
      }}>
      {busyId === item.id ? "Traitement…" : label}
    </button>
  );

  return (
    <main style={{
      minHeight: "100vh", background: "#020912", color: "#fff",
      padding: "24px 16px 60px"
    }}>
      <div style={{maxWidth: 900, margin: "0 auto"}}>
        <button onClick={() => navigate(admin ? "/admin" : "/staff")}
          style={{background: "none", border: 0, color: "#00eaff", cursor: "pointer"}}>
          ← Retour
        </button>
        <h1 style={{marginBottom: 5}}>{title}</h1>
        <p style={{color: "#9cb8c8"}}>
          Dossiers ouverts : {cases.length}
        </p>
        {error && <p role="alert" style={{color: "#ff718e"}}>{error}</p>}
        {loading ? <p>Chargement…</p> :
          cases.length === 0 ? <p>Aucun dossier ouvert.</p> :
            cases.map(item => (
              <article key={item.id} style={{
                background: "#091d2d", border: "1px solid #175269",
                borderRadius: 14, padding: 18, marginBottom: 14
              }}>
                <h2 style={{fontSize: 18, color: "#00eaff", marginTop: 0}}>
                  {item.live_title || "Live"} · #{item.live_id || "—"}
                </h2>
                {item.host_pseudo && <div>Créateur : {item.host_pseudo}</div>}
                {item.reported_pseudo &&
                  <div>Signalé par : {item.reported_pseudo}</div>}
                <p style={{lineHeight: 1.5, overflowWrap: "anywhere"}}>
                  {item.message}
                </p>
                <div style={{color: "#91aabb", fontSize: 13}}>
                  Dossier #{item.id} · {item.created_at
                    ? new Date(item.created_at).toLocaleString("fr-FR")
                    : "Date inconnue"}
                  {" · "}Live {item.live_status || "introuvable"}
                </div>
                {item.live_status === "active" && (
                  <textarea
                    aria-label={`Motif de décision pour le dossier ${item.id}`}
                    placeholder="Motif obligatoire pour avertir ou couper le live"
                    maxLength={500}
                    value={reasons[item.id] || ""}
                    onChange={event => setReasons(previous => ({
                      ...previous, [item.id]: event.target.value
                    }))}
                    style={{
                      display: "block", width: "100%", boxSizing: "border-box",
                      marginTop: 14, padding: 10, minHeight: 66,
                      borderRadius: 8, border: "1px solid #28607a",
                      background: "#04111c", color: "#fff"
                    }}
                  />
                )}
                <div style={{
                  display: "flex", flexWrap: "wrap", gap: 9, marginTop: 14
                }}>
                  {button(item, "resolve", "Classer le dossier", "#00eaff")}
                  {item.live_status === "active" && item.live_id && (
                    <>
                      {button(item, "warn", "Avertir et traiter", "#ffbf52")}
                      {button(item, "stop", "Couper et traiter", "#ff6688")}
                    </>
                  )}
                </div>
              </article>
            ))}
      </div>
    </main>
  );
}
